import React from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { HourlyStat } from '../../../utils/prop-types/engagement-types';
import styles from './styles.css';

const { convert24HourTo12Hour, getAveragePeakDwellTimeValues, formatPeakHoursRange } = require('../../../../lib/bputils').default;

const NUMBER_OF_HOURS = 23;

class PeakVisitsContent extends React.Component {
  static propTypes = {
    visitsPerHour: PropTypes.arrayOf(HourlyStat),
    avgVisitDurationPerHour: PropTypes.arrayOf(HourlyStat),
  }

  static defaultProps = {
    visitsPerHour: {},
    avgVisitDurationPerHour: {},
  }

  getChartColor = (number) => {
    if (number < 60) {
      return '#B3EDF1';
    }
    if (number < 80) {
      return '#66DAE5';
    }
    return '#02C0D3';
  }

  trimVisitorData = (hourlyVisitsMap) => {
    // in these two for loops when given the hourly visits map for a 24 hour period the two loops
    // remove the hours at the end of the 24 hr range with no data
    // and removes hours at the beginning of the 24 hr range with no data
    // start at the 23th hour and move backwards removing hours w/ no data if we encounter a hourly
    // average with data then we break out of the for loop b/c we've found the end of the range
    for (let i = NUMBER_OF_HOURS; i >= 0; i -= 1) {
      if (hourlyVisitsMap[i].visit_count === 0 && hourlyVisitsMap[i].dwell_time === 0) {
        delete hourlyVisitsMap[i];
      } else {
        break;
      }
    }
    // start at the 0th hour and move forward removing hours w/ no data if we encounter a hourly
    // average with data then we break out of the for loop b/c we've found the start of the range
    let keys = Object.keys(hourlyVisitsMap);
    const lastHour = Number(keys[keys.length - 1]);
    for (let i = 0; i <= lastHour; i += 1) {
      if (hourlyVisitsMap[i].visit_count === 0 && hourlyVisitsMap[i].dwell_time === 0) {
        delete hourlyVisitsMap[i];
      } else {
        break;
      }
    }
    keys = Object.keys(hourlyVisitsMap);
    const visitsValues = keys.map(hour => hourlyVisitsMap[hour].visit_count);
    const dwellTimeValues = keys.map(hour => hourlyVisitsMap[hour].dwell_time);
    const highestVisits = _.max(visitsValues);
    const highestDwellTime = _.max(dwellTimeValues);

    return {
      trimmedVisits: visitsValues.map((visits) => {
        const visitPercentage = Math.round((visits / highestVisits) * 100);
        return {
          y: visitPercentage,
          name: `${visitPercentage}%`,
          color: this.getChartColor(visitPercentage),
        };
      }),
      trimmedDwellTime: dwellTimeValues.map((dwellTime) => {
        const dwellTimePercentage = Math.round((dwellTime / highestDwellTime) * 100);
        return {
          y: dwellTimePercentage,
          name: dwellTime ? `${Math.floor(dwellTime / 60)}m ${dwellTime % 60}s` : 'N/A',
        };
      }),
      categories: keys.map(h => convert24HourTo12Hour(h)),
    };
  }

  getChartOptions = (visits, dwellTime, xAxisCategories) => ({
    chart: {
    },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: false },
    legend: {
      enabled: true,
      align: 'left',
      verticalAlign: 'top',
      x: 0,
      itemStyle: {
        font: '12px Roboto,"Open Sans",Helvetica,Arial,sans-serif',
        fontWeight: 'normal',
      },
    },
    xAxis: {
      categories: xAxisCategories,
      lineWidth: 0,
      tickLength: 0,
      labels: {
        step: 2,
        style: {
          fontSize: '12px',
        },
      },
    },
    yAxis: {
      gridLineWidth: 0,
      endOnTick: false,
      title: { enabled: false },
      labels: { enabled: false },
    },
    series: [{
      name: 'Visits',
      type: 'column',
      color: '#00bfd2',
      borderRadius: 3,
      pointPadding: 0,
      groupPadding: 0.05,
      data: visits,
      showInLegend: true,
      enableMouseTracking: true,
      tooltip: {
        headerFormat: '',
        pointFormat: '<strong>{series.name}:</strong> {point.name}',
      },
    }, {
      name: 'Avg Dwell Time',
      type: 'line',
      color: '#f2c506',
      data: dwellTime,
      tooltip: {
        headerFormat: '',
        pointFormat: '<strong>{series.name}:</strong> {point.name}',
      },
    }],
  })

  render() {
    const { visitsPerHour, avgVisitDurationPerHour } = this.props;
    let summaryMessage = '';
    let chartOptions;
    if (avgVisitDurationPerHour.length < 24 || visitsPerHour.length < 24) {
      summaryMessage = `Sorry, no information about peak visiting times
        could be found for the selected date range.`;
    } else {
      const hourlyMap = {};
      for (let i = 0; i <= NUMBER_OF_HOURS; i += 1) {
        hourlyMap[visitsPerHour[i].hour] = {
          visit_count: visitsPerHour[i].value,
          dwell_time: avgVisitDurationPerHour[i].value,
        };
      }

      const { trimmedVisits, trimmedDwellTime, categories } = this.trimVisitorData(hourlyMap);
      chartOptions = this.getChartOptions(trimmedVisits, trimmedDwellTime, categories);

      const { avgPeakDwellMinutes, avgPeakDwellSeconds, firstHour, endHour } = getAveragePeakDwellTimeValues(hourlyMap, 'dwell_time');
      const dwellTimeRange = formatPeakHoursRange(firstHour, endHour);
      summaryMessage = `Your peak visiting time is between ${dwellTimeRange},
           with an average dwell time of ${avgPeakDwellMinutes}m ${avgPeakDwellSeconds}s during these hours.`;
    }

    return (
      <div className={styles.peakVisitsContainer}>
        {!!chartOptions && (
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            containerProps={{ style: { height: '250px' } }}
          />
        )}
        <div className={styles.summaryTextContainer}>
          <div className={styles.imageContainer}>
            <img className={styles.circleIcon} src="/images/app/oval-border.png" alt="" />
            <img className={styles.crowdIcon} src="/images/app/crowd-icon.png" alt="" />
          </div>
          <div className={styles.text}>{summaryMessage}</div>
        </div>
      </div>
    );
  }
}

export default PeakVisitsContent;
